import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, interval, startWith, switchMap, takeUntil, catchError, of } from 'rxjs';
import { HealthCheckService } from '../services/health-check.service';

@Component({
  selector: 'app-health-indicator',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  template: `
    <div class="health-indicator" [class]="'status-' + healthIndicator" [matTooltip]="getStatusTooltip()">
      <mat-icon class="health-icon">{{ getStatusIcon() }}</mat-icon>
      <span class="health-text">{{ getStatusText() }}</span>
    </div>
  `,
  styles: [
    `
      .health-indicator {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .health-indicator:hover {
        transform: scale(1.05);
      }

      .status-online {
        background-color: #e8f5e8;
        color: #2e7d32;
      }

      .status-degraded {
        background-color: #fff3e0;
        color: #f57c00;
      }

      .status-offline {
        background-color: #ffebee;
        color: #d32f2f;
      }

      .health-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }

      .health-text {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    `,
  ],
})
export class HealthIndicatorComponent implements OnInit, OnDestroy {
  private readonly healthService = inject(HealthCheckService);
  private readonly destroy$ = new Subject<void>();

  healthIndicator: 'online' | 'degraded' | 'offline' = 'offline';

  ngOnInit(): void {
    this.startPeriodicHealthCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startPeriodicHealthCheck(): void {
    // Check health immediately and then every 60 seconds
    interval(60000)
      .pipe(
        startWith(0),
        switchMap(() => this.healthService.getHealthIndicator()),
        takeUntil(this.destroy$),
        catchError(() => of('offline' as const))
      )
      .subscribe((indicator) => {
        this.healthIndicator = indicator;
      });
  }

  getStatusIcon(): string {
    switch (this.healthIndicator) {
      case 'online':
        return 'check_circle';
      case 'degraded':
        return 'warning';
      case 'offline':
      default:
        return 'error';
    }
  }

  getStatusText(): string {
    switch (this.healthIndicator) {
      case 'online':
        return 'Online';
      case 'degraded':
        return 'Issues';
      case 'offline':
      default:
        return 'Offline';
    }
  }

  getStatusTooltip(): string {
    switch (this.healthIndicator) {
      case 'online':
        return 'System Status: All systems operational';
      case 'degraded':
        return 'System Status: Some issues detected, but functional';
      case 'offline':
      default:
        return 'System Status: Service experiencing issues';
    }
  }
}
